import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Alert, Button, Dropdown, Input, Modal, Skeleton, Typography, message } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { useStore } from '@/stores';
import type { AgentDefinition } from '@/api/types';
import { changeAgentStatus, getAgentStatusHistory, type AgentStatusChange } from '@/api/agents';
import { getEvaluationWorklist, type EvaluationWorklist } from '@/api/evaluationWorklist';
import { RESULT_STATE_LABEL } from '@/api/runEvidence';
import { trackEvent } from '@/lib/telemetry';
import {
  AGENT_STATUS_ID,
  AgentStatusPill,
  agentStatusLabel,
  agentStatusMeaning,
  agentStatusOf,
  type AgentStatusKey,
} from '@/components/agentStatus';
import { STATE_ORDER, countOf, evaluationPath } from '@/features/scenario-intelligence/workspace/worklistWords';

const { Text } = Typography;

// CHANGING AN AGENT'S STATUS FROM ITS PAGE (2 Nov 2026). Founder: "we do all the evals, when we are sure that agent is
// ready for production, we make that active." The confirm shows the latest evaluation and never blocks; the server
// records the change with the note and the counts it held (api/Agent/{id}/Status → AgentLifecycleManager).

const ORDER: AgentStatusKey[] = ['active', 'draft', 'inactive', 'archived'];

const VERB: Record<AgentStatusKey, { title: (name: string) => string; ok: string }> = {
  active: { title: (n) => `Make ${n} Active?`, ok: 'Make Active' },
  draft: { title: (n) => `Move ${n} back to Draft?`, ok: 'Move to Draft' },
  inactive: { title: (n) => `Make ${n} Inactive?`, ok: 'Make Inactive' },
  archived: { title: (n) => `Archive ${n}?`, ok: 'Archive' },
};

/** The agent's status, and a menu to change it. */
export const AgentStatusControl = observer(function AgentStatusControl({ agent }: { agent: AgentDefinition }) {
  const current = agentStatusOf(agent);
  const [target, setTarget] = useState<AgentStatusKey | null>(null);

  return (
    <>
      <Dropdown
        trigger={['click']}
        menu={{
          items: ORDER.filter((s) => s !== current).map((s) => ({
            key: s,
            label: (
              <span>
                <b>{agentStatusLabel(s)}</b>
                <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                  {agentStatusMeaning(s)}
                </Text>
              </span>
            ),
          })),
          onClick: ({ key }) => setTarget(key as AgentStatusKey),
        }}
      >
        <Button data-testid="agent-status-control">
          Status: {agentStatusLabel(current)} <DownOutlined />
        </Button>
      </Dropdown>
      <ChangeStatusModal agent={agent} target={target} onClose={() => setTarget(null)} />
    </>
  );
});

/** The confirm: what the status means, the latest evaluation (never a block), and an optional note. */
const ChangeStatusModal = observer(function ChangeStatusModal({
  agent,
  target,
  onClose,
}: {
  agent: AgentDefinition;
  target: AgentStatusKey | null;
  onClose: () => void;
}) {
  const { agentDetailStore: detail } = useStore();
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [evaluation, setEvaluation] = useState<EvaluationWorklist | null | undefined>(undefined);
  const name = agent.displayName ?? `Agent #${agent.id}`;

  useEffect(() => {
    if (target == null) return;
    setNote('');
    setError(null);
    setEvaluation(undefined);
    const ac = new AbortController();
    getEvaluationWorklist(agent.id, ac.signal)
      .then(setEvaluation)
      .catch(() => !ac.signal.aborted && setEvaluation(null)); // unread: said so, and the change is still allowed
    return () => ac.abort();
  }, [target, agent.id]);

  if (target == null) return null;

  async function confirm() {
    if (target == null) return;
    setSaving(true);
    setError(null);
    try {
      await changeAgentStatus(agent.id, target, note.trim() || undefined);
      detail.applyAgentPatch({ ...agent, statusId: AGENT_STATUS_ID[target] });
      trackEvent('Agent.StatusChanged', { agentId: agent.id, to: target });
      void message.success(`${name} is now ${agentStatusLabel(target)}.`);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The status was not changed');
    } finally {
      setSaving(false);
    }
  }

  const c = evaluation?.counts;
  const concerns = c ? c.needsAttention + c.evidenceIncomplete : 0;

  return (
    <Modal
      open
      title={VERB[target].title(name)}
      okText={VERB[target].ok}
      onOk={() => void confirm()}
      confirmLoading={saving}
      onCancel={onClose}
      destroyOnClose
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        {agentStatusLabel(target)}: {agentStatusMeaning(target).toLowerCase()}.
      </Text>

      <div data-testid="status-change-evaluation" style={{ marginBottom: 12 }}>
        <Text strong style={{ fontSize: 13 }}>Latest evaluation</Text>
        {evaluation === undefined ? (
          <Skeleton active paragraph={{ rows: 1 }} title={false} />
        ) : evaluation === null ? (
          <Text type="secondary" style={{ display: 'block', fontSize: 13 }}>
            The latest evaluation could not be read. You can still change the status; that is recorded.
          </Text>
        ) : evaluation.latestRunId == null ? (
          <Text type="secondary" style={{ display: 'block', fontSize: 13 }}>
            No evaluation has run for this agent in this practice. You can still change the status; that is recorded.
          </Text>
        ) : (
          <>
            <Text type="secondary" style={{ display: 'block', fontSize: 12.5 }}>
              Run #{evaluation.latestRunId}
              {evaluation.latestRunUtc ? ` · ${dayjs(evaluation.latestRunUtc).format('D MMM HH:mm')}` : ''} · {c!.library} library
              scenario{c!.library === 1 ? '' : 's'}
            </Text>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 4, fontSize: 13 }}>
              {STATE_ORDER.map((state) => (
                <span key={state} data-testid={`status-change-${state}`}>
                  {RESULT_STATE_LABEL[state]}: <b>{countOf(c!, state)}</b>
                </span>
              ))}
            </div>
            {target === 'active' && concerns > 0 && (
              <Alert
                style={{ marginTop: 8 }}
                type="warning"
                showIcon
                message={`${concerns} scenario${concerns === 1 ? '' : 's'} ${concerns === 1 ? 'needs' : 'need'} a look first.`}
                description="You can still make it Active. These counts are recorded with the change."
              />
            )}
          </>
        )}
        <div style={{ marginTop: 6, fontSize: 12.5 }}>
          <Link to={evaluationPath('', agent.id)} onClick={onClose}>
            Open the evaluation
          </Link>
        </div>
      </div>

      <Input.TextArea
        rows={3}
        maxLength={1000}
        showCount
        placeholder="Why (optional), e.g. evals reviewed with the oncology SME"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />

      {error && <Alert style={{ marginTop: 12 }} type="error" showIcon message={error} />}
    </Modal>
  );
});

const WORD_LABEL = (w: AgentStatusChange['from']) => (w == null ? 'never set' : agentStatusLabel(w));

/** The agent's status changes, newest first: from → to, who, when, what the evaluation said, the note. */
export const AgentStatusHistory = observer(function AgentStatusHistory({ agent }: { agent: AgentDefinition }) {
  const [rows, setRows] = useState<AgentStatusChange[] | null | undefined>(undefined);

  // Re-read whenever the status changes (a change made here patches the agent).
  useEffect(() => {
    let cancelled = false;
    setRows(undefined);
    getAgentStatusHistory(agent.id)
      .then((r) => !cancelled && setRows(r))
      .catch(() => !cancelled && setRows(null));
    return () => {
      cancelled = true;
    };
  }, [agent.id, agent.statusId]);

  if (rows === undefined) return <Skeleton active paragraph={{ rows: 1 }} title={false} />;
  if (rows === null)
    return (
      <Text type="secondary" style={{ fontSize: 12.5 }}>
        The status history could not be read.
      </Text>
    );
  if (rows.length === 0)
    return (
      <Text type="secondary" style={{ fontSize: 12.5 }} data-testid="status-history-empty">
        No status change recorded yet.
      </Text>
    );

  return (
    <div data-testid="status-history" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {rows.map((r) => (
        <div key={r.id} style={{ fontSize: 12.5 }}>
          <div>
            {WORD_LABEL(r.from)} → <AgentStatusPill agent={{ statusId: AGENT_STATUS_ID[r.to] }} />{' '}
            <Text type="secondary">
              {r.changedBy ?? `user #${r.changedById}`} · {dayjs(r.changedUtc).format('D MMM YYYY HH:mm')}
            </Text>
          </div>
          <Text type="secondary" style={{ display: 'block' }}>
            {r.evaluationRunId != null
              ? `Run #${r.evaluationRunId}: ${r.needsAttention ?? 0} need attention · ${r.evidenceIncomplete ?? 0} evidence incomplete · ${r.notAssessed ?? 0} not assessed · ${r.passed ?? 0} passed`
              : 'No evaluation at the time'}
          </Text>
          {r.note && <Text style={{ display: 'block' }}>“{r.note}”</Text>}
        </div>
      ))}
    </div>
  );
});
